import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { BookCover } from "../components/BookCover";
import { StarDisplay } from "../components/StarRating";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CheckSquare, Square, Trash2, X, ListPlus, Plus, Loader2, Library, Search, Pencil } from "lucide-react";
import { SeriesAddModal } from "../components/SeriesAddModal";
import { t, useLang } from "../lib/i18n";
import { AdSenseBanner } from "../components/AdSenseBanner";

export default function MyLibraryPage() {
  useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [showAddToList, setShowAddToList] = useState(false);
  const [showAddToSeries, setShowAddToSeries] = useState(false);
  const [seriesToExtend, setSeriesToExtend] = useState(null);
  const [shelfToEdit, setShelfToEdit] = useState(null); // {name, author}
  const [query, setQuery] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/me/library")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const avg = items.length ? (items.reduce((s, i) => s + i.my_rating, 0) / items.length).toFixed(2) : "0.00";
  const selectedCount = selected.size;
  const allSelected = items.length > 0 && selectedCount === items.length;

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set(items.map((i) => i.book.id)));

  const bulkDelete = async () => {
    if (!selectedCount) return;
    if (!window.confirm(`Remove ${selectedCount} book${selectedCount === 1 ? "" : "s"} from your library?`)) return;
    setBusy(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/books/batch-delete", { book_ids: ids });
      toast.success(`Removed ${data.deleted} book${data.deleted === 1 ? "" : "s"}`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const bulkWishlist = async () => {
    if (!selectedCount) return;
    setBusy(true);
    try {
      const ids = Array.from(selected);
      await Promise.all(ids.map((id) => api.post("/me/wishlist", { book_id: id }).catch(() => null)));
      toast.success(`Added ${selectedCount} to wish-list`);
      clearSelection();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const bulkClearSeries = async () => {
    if (!selectedCount) return;
    setBusy(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/books/batch-clear-series", { book_ids: ids });
      toast.success(`Removed ${data.updated} ${data.updated === 1 ? "book" : "books"} from their series`);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // At least one of the currently-selected books has a series_name — used to
  // enable/disable the "remove from series" button.
  const anySelectedHasSeries = useMemo(() => {
    if (!selectedCount) return false;
    return items.some((it) => selected.has(it.book.id) && (it.book.series_name || "").trim());
  }, [items, selected, selectedCount]);

  // Group books by series for the "shelves" view.
  // All distinct series names from the FULL library (not the filtered view)
  // so the "Add to series" dropdown remains useful even while the user is
  // searching for the book they want to file.
  const allSeriesNames = useMemo(() => {
    const set = new Set();
    for (const it of items) {
      const name = (it.book?.series_name || "").trim();
      if (name) set.add(name);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? items.filter((it) => {
          const b = it.book || {};
          return (b.title || "").toLowerCase().includes(q)
              || (b.author || "").toLowerCase().includes(q)
              || (b.series_name || "").toLowerCase().includes(q);
        })
      : items;
    const standalone = [];
    const series = new Map();
    for (const it of filtered) {
      const name = (it.book.series_name || "").trim();
      if (name) {
        if (!series.has(name)) series.set(name, []);
        series.get(name).push(it);
      } else {
        standalone.push(it);
      }
    }
    for (const list of series.values()) {
      list.sort((a, b) => (a.book.series_index ?? 999) - (b.book.series_index ?? 999));
    }
    return { standalone, series: Array.from(series.entries()), filteredCount: filtered.length };
  }, [items, query]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 pb-32 fade-up" data-testid="library-page">
      <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#525252]">{user.name}{t("library.eyebrow")}</div>
      <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] mt-3">{t("library.title")}</h1>

      <dl className="grid grid-cols-3 gap-6 mt-10 mb-12 max-w-md border-t border-b border-[rgba(0,0,0,0.08)] py-6">
        <Stat label={t("library.books_read")} value={items.length} testId="stat-read" />
        <Stat label={t("library.avg_rating")} value={avg} testId="stat-avg" />
        <Stat label={t("library.five_star")} value={items.filter(i => i.my_rating === 5).length} testId="stat-five" />
      </dl>

      <AdSenseBanner placement="library-inline" />

      {/* Library search */}
      {items.length > 0 && (
        <div className="relative mb-4" data-testid="library-search">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C7F65]" strokeWidth={1.5} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your library by title, author or series…"
            className="w-full bg-[#FAF5E6] border border-[rgba(10,10,10,0.18)] pl-10 pr-10 py-2.5 text-[#0A0A0A] font-sans focus:outline-none focus:border-[#0A0A0A]"
            data-testid="library-search-input"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-black/5 rounded-sm"
              aria-label="Clear search"
              data-testid="library-search-clear"
            >
              <X className="w-3.5 h-3.5 text-[#525252]" strokeWidth={1.5} />
            </button>
          )}
          {query && (
            <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mt-2" data-testid="library-search-count">
              {grouped.filteredCount} {grouped.filteredCount === 1 ? "match" : "matches"}
            </div>
          )}
        </div>
      )}

      {/* Selection toolbar */}
      {items.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3 border-y border-[rgba(0,0,0,0.08)] py-3 mb-6" data-testid="selection-toolbar">
          <button
            onClick={allSelected ? clearSelection : selectAll}
            className="inline-flex items-center gap-2 font-mono uppercase tracking-widest text-[10px] text-[#0A0A0A] hover:bg-black/5 px-2 py-1 rounded-sm"
            data-testid="select-all"
          >
            {allSelected ? <CheckSquare className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Square className="w-3.5 h-3.5" strokeWidth={1.5} />}
            {allSelected ? t("library.clear") : t("library.select_all")}
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252]" data-testid="selection-count">
              {selectedCount} selected
            </div>
            <ToolbarActions
              selectedCount={selectedCount}
              busy={busy}
              anySelectedHasSeries={anySelectedHasSeries}
              onList={() => setShowAddToList(true)}
              onSeries={() => setShowAddToSeries(true)}
              onWishlist={bulkWishlist}
              onDelete={bulkDelete}
              onClearSeries={bulkClearSeries}
              onClear={clearSelection}
              variant="top"
            />
          </div>
        </div>
      )}

      {/* Sticky bottom toolbar — mirrors the top actions so users can act
          without scrolling back up. Only mounts when something is selected. */}
      {items.length > 0 && selectedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#F2EAD3]/95 backdrop-blur-sm border-t border-[rgba(0,0,0,0.10)] shadow-[0_-6px_20px_rgba(10,10,10,0.08)]" data-testid="selection-toolbar-bottom">
          <div className="max-w-6xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252]" data-testid="selection-count-bottom">
              {selectedCount} selected
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <ToolbarActions
                selectedCount={selectedCount}
                busy={busy}
                anySelectedHasSeries={anySelectedHasSeries}
                onList={() => setShowAddToList(true)}
                onSeries={() => setShowAddToSeries(true)}
                onWishlist={bulkWishlist}
                onDelete={bulkDelete}
                onClearSeries={bulkClearSeries}
                onClear={clearSelection}
                variant="bottom"
              />
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="font-mono uppercase tracking-widest text-xs text-[#A3A3A3]">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[rgba(0,0,0,0.12)] p-16 text-center">
          <div className="font-heading italic text-2xl text-[#525252]">{t("library.empty")}</div>
          <Link to="/discover" className="font-mono uppercase tracking-widest text-xs text-[#0A0A0A] hover:underline mt-4 inline-block">{t("library.browse")}</Link>
        </div>
      ) : query && grouped.filteredCount === 0 ? (
        <div className="border border-dashed border-[rgba(0,0,0,0.12)] p-16 text-center" data-testid="library-search-empty">
          <div className="font-heading italic text-2xl text-[#525252]">No matches for &ldquo;{query}&rdquo;.</div>
          <button onClick={() => setQuery("")} className="font-mono uppercase tracking-widest text-xs text-[#0A0A0A] hover:underline mt-4 inline-block">Clear search →</button>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.series.length > 0 && grouped.series.map(([name, list]) => (
            <section key={name} data-testid={`series-${name.replace(/\s+/g,'-').toLowerCase()}`}>
              <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] mb-3">Series</div>
              <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
                <div className="flex items-center gap-2">
                  <h2 className="font-heading text-2xl text-[#0A0A0A] tracking-tight">{name}</h2>
                  <button
                    type="button"
                    onClick={() => setShelfToEdit({ name, bookIds: list.map((it) => it.book.id) })}
                    className="text-[#525252] hover:text-[#0A0A0A] p-1 -m-1"
                    aria-label={`Edit series ${name}`}
                    data-testid={`edit-shelf-${name.replace(/\s+/g,'-').toLowerCase()}`}
                  >
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
                <button
                  onClick={() => setSeriesToExtend({ name, author: list[0]?.book?.author || "" })}
                  className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm transition-colors flex-shrink-0"
                  data-testid={`extend-series-${name.replace(/\s+/g,'-').toLowerCase()}`}
                >
                  <Library className="w-3 h-3" strokeWidth={1.5} /> Add more in this series
                </button>
              </div>
              <BookRail items={list} selected={selected} onToggle={toggle} testId={`series-rail-${name.replace(/\s+/g,'-').toLowerCase()}`} />
            </section>
          ))}
          {grouped.standalone.length > 0 && (
            <section data-testid="standalone-books">
              {grouped.series.length > 0 && (
                <h2 className="font-heading text-2xl text-[#0A0A0A] mb-5 tracking-tight">Other books</h2>
              )}
              <BookRail items={grouped.standalone} selected={selected} onToggle={toggle} testId="standalone-rail" />
            </section>
          )}
        </div>
      )}

      <AddToListModal
        open={showAddToList}
        onClose={() => setShowAddToList(false)}
        bookIds={Array.from(selected)}
        onDone={() => { setShowAddToList(false); clearSelection(); }}
      />

      <AddToSeriesModal
        open={showAddToSeries}
        onClose={() => setShowAddToSeries(false)}
        bookIds={Array.from(selected)}
        existingSeries={allSeriesNames}
        onDone={() => { setShowAddToSeries(false); clearSelection(); load(); }}
      />

      <SeriesAddModal
        open={!!seriesToExtend}
        onClose={() => setSeriesToExtend(null)}
        seriesName={seriesToExtend?.name}
        author={seriesToExtend?.author}
        onAdded={() => { setSeriesToExtend(null); load(); }}
      />

      <EditShelfSeriesModal
        open={!!shelfToEdit}
        shelf={shelfToEdit}
        onClose={() => setShelfToEdit(null)}
        onDone={() => { setShelfToEdit(null); load(); }}
      />
    </div>
  );
}

function ToolbarActions({ selectedCount, busy, anySelectedHasSeries, onList, onSeries, onWishlist, onDelete, onClearSeries, onClear, variant }) {
  const suffix = variant === "bottom" ? "-bottom" : "";
  return (
    <>
      <button
        onClick={onList}
        disabled={!selectedCount || busy}
        className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
        data-testid={`bulk-add-to-list${suffix}`}
      >
        <ListPlus className="w-3 h-3" strokeWidth={1.5} /> {t("library.add_to_list")}
      </button>
      <button
        onClick={onSeries}
        disabled={!selectedCount || busy}
        className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
        data-testid={`bulk-add-to-series${suffix}`}
      >
        <Library className="w-3 h-3" strokeWidth={1.5} /> {t("library.add_to_series")}
      </button>
      <button
        onClick={onClearSeries}
        disabled={!anySelectedHasSeries || busy}
        className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
        data-testid={`bulk-clear-series${suffix}`}
      >
        {t("library.remove_from_series")}
      </button>
      <button
        onClick={onWishlist}
        disabled={!selectedCount || busy}
        className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
        data-testid={`bulk-wishlist${suffix}`}
      >
        {t("library.add_to_wishlist")}
      </button>
      <button
        onClick={onDelete}
        disabled={!selectedCount || busy}
        className="inline-flex items-center gap-1.5 border border-[#7C1D1D]/30 bg-[#FAF5E6] hover:bg-[#7C1D1D]/5 text-[#7C1D1D] font-mono uppercase tracking-widest text-[10px] px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
        data-testid={`bulk-delete${suffix}`}
      >
        <Trash2 className="w-3 h-3" strokeWidth={1.5} /> {t("library.remove")}
      </button>
      {selectedCount > 0 && (
        <button onClick={onClear} className="p-1.5 hover:bg-black/5 rounded-sm" aria-label="Clear selection" data-testid={`clear-selection${suffix}`}>
          <X className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
      )}
    </>
  );
}


function AddToSeriesModal({ open, onClose, bookIds, existingSeries = [], onDone }) {
  const [name, setName] = useState("");
  const [startIndex, setStartIndex] = useState("");
  const [saving, setSaving] = useState(false);
  // When the user already has series, default to picking from the list.
  // When they don't, we start straight in "new series" mode.
  const [creatingNew, setCreatingNew] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setStartIndex("");
      setSaving(false);
      setCreatingNew(existingSeries.length === 0);
    }
  }, [open, existingSeries.length]);

  if (!open) return null;

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || !bookIds.length) return;
    setSaving(true);
    try {
      const body = { book_ids: bookIds, series_name: trimmed };
      const parsed = startIndex.trim() === "" ? null : Number(startIndex);
      if (parsed != null && !Number.isNaN(parsed)) body.start_index = parsed;
      const { data } = await api.post("/books/batch-set-series", body);
      toast.success(`Added ${data.updated} ${data.updated === 1 ? "book" : "books"} to "${data.series_name}"`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="add-to-series-modal"
    >
      <div
        className="bg-[#F2EAD3] max-w-md w-full max-h-[80vh] overflow-y-auto border border-[rgba(10,10,10,0.10)] shadow-[0_20px_60px_rgba(10,10,10,0.20)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-[rgba(10,10,10,0.08)] bg-[#F2EAD3]">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] mb-2 inline-flex items-center gap-2">
              <Library className="w-3 h-3" strokeWidth={1.5} /> Add to series
            </div>
            <h3 className="font-heading text-2xl text-[#0A0A0A] tracking-tight">
              {bookIds.length} {bookIds.length === 1 ? "book" : "books"} selected
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/5 rounded-sm"
            aria-label="Close"
            data-testid="add-to-series-close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <p className="text-sm text-[#525252] leading-relaxed">
            Group these books under a series. Pick one of your existing series, or start a new one — it&apos;ll show up on your shelf as its own row.
          </p>

          <div>
            <label htmlFor="series-name-input" className="block font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-2">
              Series name
            </label>

            {creatingNew || existingSeries.length === 0 ? (
              <input
                id="series-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Mistborn"
                className="w-full bg-[#FAF5E6] border border-[rgba(10,10,10,0.18)] px-3 py-2.5 text-[#0A0A0A] font-sans focus:outline-none focus:border-[#0A0A0A]"
                data-testid="add-to-series-name"
                autoFocus
              />
            ) : (
              <select
                id="series-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-[#FAF5E6] border border-[rgba(10,10,10,0.18)] px-3 py-2.5 text-[#0A0A0A] font-sans focus:outline-none focus:border-[#0A0A0A]"
                data-testid="add-to-series-name"
              >
                <option value="">— Pick a series —</option>
                {existingSeries.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}

            {existingSeries.length > 0 && (
              <button
                type="button"
                onClick={() => { setCreatingNew((v) => !v); setName(""); }}
                className="mt-3 inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] text-[#0A0A0A] hover:underline"
                data-testid="add-to-series-toggle-new"
              >
                <Plus className="w-3 h-3" strokeWidth={1.5} />
                {creatingNew ? "Pick from existing series instead" : "Add to new series?"}
              </button>
            )}
          </div>

          <div>
            <label htmlFor="series-start-input" className="block font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-2">
              Starting number <span className="normal-case tracking-normal text-[#8C7F65]">(optional)</span>
            </label>
            <input
              id="series-start-input"
              type="number"
              step="any"
              value={startIndex}
              onChange={(e) => setStartIndex(e.target.value)}
              placeholder="1"
              className="w-32 bg-[#FAF5E6] border border-[rgba(10,10,10,0.18)] px-3 py-2.5 text-[#0A0A0A] font-sans focus:outline-none focus:border-[#0A0A0A]"
              data-testid="add-to-series-start-index"
            />
            <p className="text-xs text-[#8C7F65] mt-2">
              If set, the books will be numbered sequentially in the order you selected them (1, 2, 3…). Leave blank to keep existing numbers.
            </p>
          </div>
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-[rgba(10,10,10,0.08)]">
            <button
              onClick={onClose}
              className="font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A] px-3 py-2"
              data-testid="add-to-series-cancel"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={saving || !name.trim() || !bookIds.length}
              className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm transition-colors disabled:opacity-50"
              data-testid="add-to-series-submit"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : <Library className="w-3.5 h-3.5" strokeWidth={1.5} />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Rename or delete an entire series shelf (all books at once). Delete here
// means "un-group these books from this series", not "delete the books" —
// the books stay in the library, they just move to the standalone shelf.
function EditShelfSeriesModal({ open, shelf, onClose, onDone }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open && shelf) {
      setName(shelf.name || "");
      setSaving(false);
      setDeleting(false);
    }
  }, [open, shelf]);

  if (!open || !shelf) return null;

  const bookCount = shelf.bookIds?.length || 0;

  const rename = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === shelf.name || bookCount === 0) return;
    setSaving(true);
    try {
      const { data } = await api.post("/books/batch-set-series", {
        book_ids: shelf.bookIds,
        series_name: trimmed,
      });
      toast.success(`Renamed to "${data.series_name}"`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeShelf = async () => {
    if (!window.confirm(`Un-group the "${shelf.name}" series? The ${bookCount} books stay in your library — they just stop being grouped.`)) return;
    setDeleting(true);
    try {
      await api.post("/books/batch-clear-series", { book_ids: shelf.bookIds });
      toast.success(`Series "${shelf.name}" removed`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="edit-shelf-modal"
    >
      <div
        className="bg-[#F2EAD3] max-w-md w-full max-h-[85vh] overflow-y-auto border border-[rgba(10,10,10,0.10)] shadow-[0_20px_60px_rgba(10,10,10,0.20)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-[rgba(10,10,10,0.08)]">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] mb-2 inline-flex items-center gap-2">
              <Library className="w-3 h-3" strokeWidth={1.5} /> Edit series
            </div>
            <h3 className="font-heading text-2xl text-[#0A0A0A] tracking-tight leading-tight">{shelf.name}</h3>
            <div className="font-mono uppercase tracking-widest text-[10px] text-[#8C7F65] mt-1">{bookCount} {bookCount === 1 ? "book" : "books"}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-sm" aria-label="Close" data-testid="edit-shelf-close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label htmlFor="edit-shelf-name" className="block font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-2">Rename series</label>
            <input
              id="edit-shelf-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-[#FAF5E6] border border-[rgba(10,10,10,0.18)] px-3 py-2.5 text-[#0A0A0A] font-sans focus:outline-none focus:border-[#0A0A0A]"
              data-testid="edit-shelf-name"
              autoFocus
            />
            <p className="text-xs text-[#8C7F65] mt-2">
              This will update every book on the shelf.
            </p>
          </div>

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-[rgba(10,10,10,0.08)] flex-wrap">
            <button
              type="button"
              onClick={removeShelf}
              disabled={saving || deleting}
              className="inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] text-[#8A0F0F] hover:text-[#5C0808] underline underline-offset-4 disabled:opacity-50"
              data-testid="edit-shelf-remove"
            >
              <Trash2 className="w-3 h-3" strokeWidth={1.5} />
              {deleting ? "Removing…" : "Remove series"}
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A] px-3 py-2"
                data-testid="edit-shelf-cancel"
              >
                Cancel
              </button>
              <button
                onClick={rename}
                disabled={saving || deleting || !name.trim() || name.trim() === shelf.name}
                className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm transition-colors disabled:opacity-50"
                data-testid="edit-shelf-save"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : <Library className="w-3.5 h-3.5" strokeWidth={1.5} />}
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}



function AddToListModal({ open, onClose, bookIds, onDone }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setNewName("");
    api.get("/sections")
      .then(({ data }) => setLists(data || []))
      .catch(() => setLists([]))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const addAllToList = async (sectionId, sectionName) => {
    if (!bookIds.length || adding) return;
    setAdding(true);
    try {
      const results = await Promise.allSettled(
        bookIds.map((id) => api.post(`/sections/${sectionId}/books`, { book_id: id }))
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      if (ok > 0) toast.success(`Added ${ok} ${ok === 1 ? "book" : "books"} to "${sectionName}"`);
      if (failed > 0) toast.error(`${failed} couldn't be added.`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  const createAndAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const { data } = await api.post("/sections", { name });
      await addAllToList(data.id, data.name);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
      data-testid="add-to-list-modal"
    >
      <div
        className="bg-[#F2EAD3] max-w-md w-full max-h-[80vh] overflow-y-auto border border-[rgba(10,10,10,0.10)] shadow-[0_20px_60px_rgba(10,10,10,0.20)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-[rgba(10,10,10,0.08)] sticky top-0 bg-[#F2EAD3]">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] mb-2 inline-flex items-center gap-2">
              <ListPlus className="w-3 h-3" strokeWidth={1.5} /> Add to list
            </div>
            <h3 className="font-heading text-2xl text-[#0A0A0A] tracking-tight">
              {bookIds.length} {bookIds.length === 1 ? "book" : "books"} selected
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/5 rounded-sm"
            aria-label="Close"
            data-testid="add-to-list-close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {loading ? (
            <div className="font-mono uppercase tracking-widest text-xs text-[#A3A3A3] text-center py-6">
              <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" strokeWidth={1.5} /> Loading lists…
            </div>
          ) : (
            <>
              {lists.length > 0 ? (
                <ul className="space-y-2" data-testid="add-to-list-options">
                  {lists.map((list) => (
                    <li key={list.id}>
                      <button
                        onClick={() => addAllToList(list.id, list.name)}
                        disabled={adding}
                        className="w-full flex items-center justify-between p-3 border border-[rgba(10,10,10,0.10)] bg-[#FAF5E6] hover:border-[#0A0A0A] hover:bg-white text-left transition-colors disabled:opacity-50"
                        data-testid={`add-to-list-option-${list.id}`}
                      >
                        <span className="font-heading text-base text-[#0A0A0A]">{list.name}</span>
                        <span className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">
                          {(list.book_ids || []).length} books
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#525252] italic">You don&apos;t have any lists yet. Create one below.</p>
              )}

              <div className="border-t border-[rgba(10,10,10,0.08)] pt-4">
                <label className="block font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-2">
                  Or create a new list
                </label>
                <div className="flex items-stretch gap-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g. Summer 2026"
                    className="flex-1 bg-white border border-[rgba(10,10,10,0.18)] px-3 py-2 text-sm font-sans rounded-sm focus:outline-none focus:border-[#0A0A0A]"
                    data-testid="add-to-list-new-name"
                    onKeyDown={(e) => { if (e.key === "Enter") createAndAdd(); }}
                  />
                  <button
                    onClick={createAndAdd}
                    disabled={!newName.trim() || creating || adding}
                    className="inline-flex items-center gap-1.5 bg-[#0A0A0A] hover:bg-[#1F1F1F] disabled:opacity-50 text-[#F2EAD3] font-mono uppercase tracking-widest text-[10px] px-4 py-2 rounded-sm transition-colors"
                    data-testid="add-to-list-create"
                  >
                    {creating ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} /> : <Plus className="w-3 h-3" strokeWidth={1.5} />}
                    Create & add
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BookRail({ items, selected, onToggle, testId }) {
  // Horizontal swipe/scroll rail. Each tile has a fixed width so the row
  // stays scrollable no matter how many books are in the series. Snap-x
  // gives touch users a natural page-like feel; the CSS-only right-edge
  // fade hints that there's more content off-screen.
  return (
    <div className="relative -mx-6 md:mx-0" data-testid={testId}>
      <div
        className="flex gap-5 md:gap-7 overflow-x-auto snap-x snap-mandatory pb-4 pl-6 md:pl-0 pr-6 md:pr-8 hide-scrollbar scroll-smooth"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {items.map(({ book, my_rating, my_text }) => (
          <div key={book.id} className="snap-start flex-shrink-0 w-[42vw] max-w-[180px] md:w-[180px] lg:w-[200px]">
            <BookTile book={book} my_rating={my_rating} my_text={my_text} selected={selected} onToggle={onToggle} />
          </div>
        ))}
      </div>
      {/* Right-edge gradient hint that more content is scrollable */}
      <div className="pointer-events-none absolute top-0 bottom-4 right-0 w-10 bg-gradient-to-l from-[#F2EAD3] to-transparent hidden md:block" aria-hidden="true" />
    </div>
  );
}

function BookTile({ book, my_rating, my_text, selected, onToggle }) {
  const isSelected = selected.has(book.id);
  return (
    <div className="relative group" data-testid={`library-book-${book.id}`}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); onToggle(book.id); }}
        className={`absolute z-10 top-2 left-2 w-7 h-7 rounded-sm flex items-center justify-center transition-colors ${isSelected ? "bg-[#0A0A0A] text-[#F2EAD3]" : "bg-[#F2EAD3]/90 text-[#0A0A0A] border border-[rgba(10,10,10,0.18)] opacity-0 group-hover:opacity-100"}`}
        aria-label={isSelected ? "Deselect" : "Select"}
        data-testid={`select-${book.id}`}
      >
        {isSelected ? <CheckSquare className="w-4 h-4" strokeWidth={1.5} /> : <Square className="w-4 h-4" strokeWidth={1.5} />}
      </button>
      <Link
        to={`/books/${book.id}`}
        onClick={(e) => { if (selected.size > 0) { e.preventDefault(); onToggle(book.id); } }}
      >
        <BookCover src={book.cover_url} title={book.title} author={book.author} />
        <div className="mt-3 space-y-1">
          <div className="font-heading text-base text-[#0A0A0A] leading-tight line-clamp-2">
            {book.title}
            {book.series_index != null && book.series_name && (
              <span className="font-mono text-[10px] text-[#525252] ml-1">#{book.series_index}</span>
            )}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{book.author}</div>
          <div className="flex items-center gap-2 pt-1">
            <StarDisplay value={my_rating} size={12} />
            <span className="font-mono text-[10px] text-[#0A0A0A]">my rating</span>
          </div>
          {my_text && <div className="text-xs text-[#525252] line-clamp-2 italic mt-1">&ldquo;{my_text}&rdquo;</div>}
        </div>
      </Link>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div>
      <dt className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{label}</dt>
      <dd className="font-heading text-3xl text-[#0A0A0A] mt-1" data-testid={testId}>{value}</dd>
    </div>
  );
}
